import { useLocations } from '@my/directory-api/hooks';
import type { Address } from '@my/directory-api';
import { tmsApi } from '@my/tms-api';
import type { CargoDefinition, Order, ServiceTag } from '@my/tms-api';
import {
  useCargoDefinitions,
  usePreviousOrders,
  useServiceTags,
  orderTaskKeys,
} from '@my/tms-api/hooks';
import { ErrorBanner, Loading, cn } from '@my/ui';
import { useQueries } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { CargoCell, CargoInfoCell, RouteStrip, TaskDateTime } from './OrderListCells';
import { OrderStatusPill } from './OrderStatusPill';

/**
 * New order → "Start from a previous order": the customer's earlier orders,
 * latest run first, a few at a time — more load as the list is scrolled.
 *
 * <p>
 * The server does the choosing: Open / In progress / Completed only (a draft
 * was never confirmed, a cancelled order didn't happen), narrowed to the
 * picked cargo and variant, sorted by when each order runs, and paged. Each
 * row's tasks come from the same cache the Orders screen fills, so an order
 * already seen there costs nothing.
 * </p>
 *
 * <p>
 * Picking a row selects it for the panel beside the list; the first order is
 * selected as soon as the list arrives, so the right side is never empty
 * while there's something to show.
 * </p>
 */
export function PreviousOrdersPanel({
  organisationId,
  customerPartyId,
  customerName,
  cargoDefinitionId,
  cargoVariantId,
  cargoLabel,
  selectedOrderId,
  onSelect,
  headerAside,
}: {
  organisationId: string | null;
  customerPartyId: string;
  customerName: string;
  /** Narrows the list once a cargo is picked; null lists every cargo. */
  cargoDefinitionId: string | null;
  /** And to the variant, when the pick names one (e.g. Iso Tank 40ft). */
  cargoVariantId: string | null;
  /** The picked cargo as the picker shows it, for the heading. */
  cargoLabel: string | null;
  selectedOrderId: string | null;
  onSelect: (order: Order | null) => void;
  /** The Previous orders | Templates switch, when templates are on. */
  headerAside?: ReactNode;
}) {
  const query = usePreviousOrders(
    organisationId
      ? {
          organisationId,
          customerPartyId,
          cargoDefinitionId,
          cargoDefinitionVariantId: cargoVariantId,
        }
      : null,
  );
  const orders = useMemo(() => query.data?.orders ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;

  const cargoDefsQuery = useCargoDefinitions(organisationId);
  const serviceTagsQuery = useServiceTags(organisationId);
  const locationsQuery = useLocations(organisationId);

  // Same key + queryFn as `useOrderTasks`, so the cache is shared with the
  // Orders screen and the order page.
  const taskQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!organisationId,
      staleTime: 30 * 1000,
    })),
  });

  const cargoDefsById = useMemo(() => {
    const map = new Map<string, CargoDefinition>();
    for (const d of cargoDefsQuery.data ?? []) map.set(d.id, d);
    return map;
  }, [cargoDefsQuery.data]);
  const serviceTagsById = useMemo(() => {
    const map = new Map<string, ServiceTag>();
    for (const t of serviceTagsQuery.data ?? []) map.set(t.id, t);
    return map;
  }, [serviceTagsQuery.data]);
  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [locationsQuery.data]);

  // Keep a selection whenever there's a list: the first order when nothing
  // is picked yet, or when the picked one is no longer in it (another cargo
  // was chosen, say). Nothing selected when the list is empty.
  useEffect(() => {
    if (!query.isSuccess) return;
    if (orders.length === 0) {
      if (selectedOrderId !== null) onSelect(null);
      return;
    }
    if (!orders.some((o) => o.id === selectedOrderId)) onSelect(orders[0]!);
  }, [query.isSuccess, orders, selectedOrderId, onSelect]);

  // The next page loads when the operator SCROLLS near the end of the list —
  // on the scroll itself, not on the end merely being in view. Watching for
  // visibility loaded a second page on open: while the first rows were still
  // waiting for their tasks they were short enough for the end to show.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const box = e.currentTarget;
    if (!hasNextPage || isFetchingNextPage) return;
    if (box.scrollTop + box.clientHeight >= box.scrollHeight - 80) void fetchNextPage();
  };

  // The list fills the height of the panel beside it, which often holds more
  // than one page. Rather than leave it half empty, keep loading until it
  // overflows — then scrolling takes over. Only once every loaded row has its
  // tasks: a row still waiting is shorter than it will be, and measuring then
  // would over-fetch.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rowsSettled = taskQueries.every((q) => !q.isPending);
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || !hasNextPage || isFetchingNextPage || !rowsSettled) return;
    if (box.scrollHeight <= box.clientHeight + 1) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, rowsSettled, orders.length, fetchNextPage]);

  const filterNote = cargoLabel ? ` · ${cargoLabel}` : '';

  return (
    <section className="flex h-full flex-col rounded-md border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-3 py-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Start from a previous order{' '}
            {query.isSuccess && <span className="text-slate-400">({totalCount})</span>}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {customerName}’s orders{filterNote}, latest first. Drafts and cancelled orders aren’t
            listed.
          </p>
        </div>
        {headerAside}
      </header>

      {query.isPending && (
        <div className="p-3">
          <Loading label="Loading previous orders" />
        </div>
      )}
      {query.isError && (
        <div className="p-3">
          <ErrorBanner
            title="Couldn’t load previous orders"
            message={query.error?.message ?? 'Unknown error'}
          />
        </div>
      )}
      {query.isSuccess && orders.length === 0 && (
        <p className="px-3 py-6 text-center text-xs italic text-slate-400">
          {cargoLabel
            ? `No previous orders for ${customerName} with ${cargoLabel}.`
            : `No previous orders for ${customerName} yet.`}
        </p>
      )}

      {query.isSuccess && orders.length > 0 && (
        // Side by side it takes whatever height is left in the panel (which
        // fills the row); stacked on narrow screens it keeps a fixed height.
        <div
          ref={scrollRef}
          onScroll={onListScroll}
          className="max-h-[18rem] min-h-0 flex-1 overflow-auto xl:max-h-none"
        >
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Order</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Date</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Cargo</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Route</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-semibold">Tasks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((order, i) => {
                const q = taskQueries[i];
                const tasks = (q?.data ?? []).slice().sort((a, b) => a.sequence - b.sequence);
                const pending = q?.isPending ?? true;
                const cargoDef = cargoDefsById.get(order.cargoDefinitionId);
                const start = tasks[0];
                const selected = order.id === selectedOrderId;
                return (
                  <tr
                    key={order.id}
                    tabIndex={0}
                    aria-selected={selected}
                    onClick={() => onSelect(order)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(order);
                      }
                    }}
                    className={cn(
                      'cursor-pointer align-top outline-none focus-visible:bg-slate-50',
                      selected ? 'bg-blue-50' : 'hover:bg-slate-50',
                    )}
                  >
                    {/* The selected row's marker is a left edge on its first
                        cell — row borders and shadows render unreliably on
                        table rows. */}
                    <td
                      className={cn(
                        'whitespace-nowrap border-l-2 px-3 py-2.5',
                        selected ? 'border-l-blue-500' : 'border-l-transparent',
                      )}
                    >
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-slate-900">
                          {order.orderNumber}
                        </span>
                        <div>
                          <OrderStatusPill status={order.status} />
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {start ? (
                        <TaskDateTime task={start} />
                      ) : (
                        <span className="text-[11px] italic text-slate-300">
                          {pending ? '…' : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="space-y-1">
                        <CargoCell
                          order={order}
                          cargoDef={cargoDef}
                          serviceTagsById={serviceTagsById}
                        />
                        {/* No Tank / BIC here — a copy never carries the unit. */}
                        <CargoInfoCell
                          order={order}
                          cargoDef={cargoDef}
                          tasks={tasks}
                          pending={pending}
                          hideUnits
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <RouteStrip tasks={tasks} addressById={addressById} pending={pending} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-semibold text-slate-700">
                      {pending ? (
                        <span className="font-normal italic text-slate-300">…</span>
                      ) : (
                        tasks.length
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Also reachable without scrolling — keyboard users, or a page
              whose rows happen to fit. */}
          {hasNextPage && !isFetchingNextPage && (
            <div className="px-3 py-2 text-center">
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                className="rounded-sm px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50"
              >
                Show more
              </button>
            </div>
          )}
          {isFetchingNextPage && (
            <p className="flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Loading more orders…
            </p>
          )}
          {!hasNextPage && orders.length > 5 && (
            <p className="px-3 py-2 text-center text-[11px] italic text-slate-400">
              That’s every previous order for {customerName}.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
